import _ from "lodash";
import moment from "moment";

// V4
export function transformHomeDashboardData(homeDashboardData) {
  if (!homeDashboardData) return;
  let data = homeDashboardData;

  // Use USD if publisher has no default reporting currency
  const defaultCurrency = _.find(homeDashboardData.exchangeRates, {
    isDefault: true,
  });
  data.defaultReportingCurrency = defaultCurrency
    ? defaultCurrency.currency
    : "USD";
  data.exchangeRates = _transformExchangeRates(data.exchangeRates);

  data.gamNetworkStatusReport = _transformToJson(data.gamNetworkStatusReport);
  data.gamNetworkPerformanceReport = _transformToJson(
    data.gamNetworkPerformanceReport,
  );
  data.yieldSetStatusReport = _transformToJson(data.yieldSetStatusReport);
  data.yieldSetPerformanceReport = _transformToJson(
    data.yieldSetPerformanceReport,
  );

  const pubIdByNetworkId = _.reduce(
    data.gamNetworks,
    (result, n) => {
      result[n.id] = n.pubId;
      return result;
    },
    {},
  );

  const requestTypes = [];

  _.forEach(data.gamNetworkPerformanceReport, (r) => {
    requestTypes.push(r.requestType);

    // r.date: "2021-05-25T00:00:00.000Z"
    r.date = r.date.slice(0, 10); // 2021-05-25
    // r.date = moment.utc(r.date).format("YYYY-MM-DD");
    r.dateMs = moment.utc(r.date).unix();

    // to simplify filter
    r.pubId = pubIdByNetworkId[r.gamNetworkId];

    // all metrics are string
    r.totalRequests = _.parseInt(r.totalRequests);
    r.protectedRequests = _.parseInt(r.protectedRequests);
    r.boostingRequests = _.parseInt(r.boostingRequests);
    r.onboardedRequests = _.parseInt(r.onboardedRequests);
    r.notProtectedRequests = _.parseInt(r.notProtectedRequests);
    r.protectedOriginalRevenue = _.parseInt(r.protectedOriginalRevenue);
    r.originalRevenue = _.parseInt(r.originalRevenue);
    r.increasedRevenue = _.parseInt(r.increasedRevenue);
    r.cost = _.parseInt(r.cost);
  });

  _.forEach(data.yieldSetPerformanceReport, (r) => {
    requestTypes.push(r.requestType);

    // r.date: "2021-05-25T00:00:00.000Z"
    r.date = r.date.slice(0, 10); // 2021-05-25
    // r.date = moment.utc(r.date).format("YYYY-MM-DD");
    r.dateMs = moment.utc(r.date).unix();

    // to simplify filter
    r.pubId = pubIdByNetworkId[r.gamNetworkId];

    // all metrics are string
    r.totalRequests = _.parseInt(r.totalRequests);
    r.protectedRequests = _.parseInt(r.protectedRequests);
    r.boostingRequests = _.parseInt(r.boostingRequests);
    r.onboardedRequests = _.parseInt(r.onboardedRequests);
    r.notProtectedRequests = _.parseInt(r.notProtectedRequests);
    r.protectedOriginalRevenue = _.parseInt(r.protectedOriginalRevenue);
    r.originalRevenue = _.parseInt(r.originalRevenue);
    r.increasedRevenue = _.parseInt(r.increasedRevenue);
    r.cost = _.parseInt(r.cost);
  });

  _.forEach(data.gamNetworkStatusReport, (r) => {
    // to simplify filter
    r.pubId = pubIdByNetworkId[r.gamNetworkId];

    r.compatibleRequests = _.parseInt(r.compatibleRequests); // network only
    r.compatibleUnitCount = _.parseInt(r.compatibleUnitCount);
    r.onboardedRequests = _.parseInt(r.onboardedRequests);
    r.onboardedUnitCount = _.parseInt(r.onboardedUnitCount);
    r.runningRequests = _.parseInt(r.runningRequests); // v2 shows "protected requests"
    r.runningUnitCount = _.parseInt(r.runningUnitCount); // v2 shows "protected unit"
    r.totalUnitCount = _.parseInt(r.totalUnitCount);

    // newly added for v2
    r.currentSegments = _.parseInt(r.currentSegments);
    r.totalSegmentBatches = _.parseInt(r.totalSegmentBatches);
    r.totalExperiments = _.parseInt(r.totalExperiments);
    r.totalExperimentGroups = _.parseInt(r.totalExperimentGroups);
    r.averageExperimentsPerDay = _.parseInt(r.averageExperimentsPerDay);
    r.averageExperimentGroupsPerDay = _.parseInt(
      r.averageExperimentGroupsPerDay,
    );
  });

  _.forEach(data.yieldSetStatusReport, (r) => {
    // to simplify filter
    r.pubId = pubIdByNetworkId[r.gamNetworkId];

    r.onboardedUnitCount = _.parseInt(r.onboardedUnitCount);
    r.runningUnitCount = _.parseInt(r.runningUnitCount); // v2 shows "protected unit"
    r.onboardedRequests = _.parseInt(r.onboardedRequests); // yieldset only
    r.runningRequests = _.parseInt(r.runningRequests); // v2 shows "protected requests"

    // newly added for v2
    r.currentSegments = _.parseInt(r.currentSegments);
    r.totalSegmentBatches = _.parseInt(r.totalSegmentBatches);
    r.totalExperiments = _.parseInt(r.totalExperiments);
    r.totalExperimentGroups = _.parseInt(r.totalExperimentGroups);
    r.averageExperimentsPerDay = _.parseInt(r.averageExperimentsPerDay);
    r.averageExperimentGroupsPerDay = _.parseInt(
      r.averageExperimentGroupsPerDay,
    );
  });

  const publishers = _.map(data.publishers, (pub) => {
    const pubNetworks = _.filter(data.gamNetworks, { pubId: pub.id });
    pub.networkIds = _.map(pubNetworks, "id");
    pub.hasPerfData = _.some(pubNetworks, { hasPerfData: true });
    return pub;
  });

  data.publishers = _.orderBy(
    publishers,
    ["hasPerfData", "id"],
    ["desc", "asc"],
  );

  data.gamNetworks = _.orderBy(
    data.gamNetworks,
    ["hasPerfData", "id"],
    ["desc", "asc"],
  );

  data.yieldSets = _.orderBy(
    data.yieldSets,
    ["hasPerfData", "id"],
    ["desc", "asc"],
  );

  data.requestTypes = _.sortBy(_.uniq(requestTypes));

  return data;
}

// V4
export function calculateTrendData({
  homeDashboardData,
  selectedPubId,
  selectedNetworkId,
  selectedYieldSetIds,
  selectedRequestType,
  startDate,
  endDate,
  exchangeRates,
}) {
  // --- Revenue Trend ---
  // For "Revenue with Yieldbooster": (notProtectedRevenue + protectedOriginalRevenue + increasedRevenue)
  // For "Revenue without Yieldbooster": (notProtectedRevenue + protectedOriginalRevenue)
  // For "Extra Ad Serving Cost": (cost)
  // For "Net Increased Revenue": increasedRevenue - cost
  // For "Perceived Lift": (100 * (increasedRevenue - cost) / protectedOriginalRevenue [%])
  // For "Total Requests" : totalRequests
  // For "Original Revenue": protectedOriginalRevenue
  // For "Not-Protected Revenue": notProtectedRevenue

  // --- Request Protection Index ---
  // For "Protected Score": 100 * protectedRequests / totalRequest
  // For "Defended Score": 100 * boostingRequests / protectedRequests

  // --- Revenue Lift ---
  // For "Secured Lift": 100 * (increasedRevenue - cost) / originalRevenue
  // For "Perceived Lift": 100 * (increasedRevenue - cost) / protectedOriginalRevenue

  // --- Request Funnel ---
  // For "Compatible Requests": totalRequests
  // For "Protected Requests": protectedRequests
  // For "Defended Requests": boostingRequests

  // --- Secured Request RPM ---
  // For "Secured Request RPM": (increasedRevenue + originalRevenue) / (boostingRequests / 1000)
  // For "Benchmark Request RPM": originalRevenue / (boostingRequests / 1000)
  let reports = [];
  if (selectedPubId === -1) {
    reports = _.filter(homeDashboardData.gamNetworkPerformanceReport, (r) => {
      return _isDateBetween({ date: r.date, startDate, endDate });
    });
  } else if (
    selectedPubId !== -1 &&
    selectedNetworkId === -1 &&
    selectedYieldSetIds === -1
  ) {
    reports = _.filter(homeDashboardData.gamNetworkPerformanceReport, (r) => {
      return (
        _isDateBetween({ date: r.date, startDate, endDate }) &&
        r.pubId === selectedPubId
      );
    });
  } else if (selectedNetworkId !== -1 && selectedYieldSetIds === -1) {
    reports = _.filter(homeDashboardData.gamNetworkPerformanceReport, (r) => {
      return (
        _isDateBetween({ date: r.date, startDate, endDate }) &&
        r.gamNetworkId === selectedNetworkId
      );
    });
  } else if (selectedYieldSetIds !== -1) {
    reports = _.filter(homeDashboardData.yieldSetPerformanceReport, (r) => {
      return (
        _isDateBetween({ date: r.date, startDate, endDate }) &&
        _.indexOf(selectedYieldSetIds, r.yieldSetId) !== -1
      );
    });
  }

  // filter by request type
  if (!_.isNil(selectedRequestType) && selectedRequestType !== -1) {
    reports = _.filter(reports, (r) => {
      return _.indexOf(selectedRequestType, r.requestType) !== -1;
    });
  }

  // by date
  let dataMap = {};
  _.forEach(reports, (r) => {
    if (!dataMap[r.date]) {
      dataMap[r.date] = {
        date: r.date,
        dateMs: r.dateMs,

        notProtectedRevenue: 0,
        protectedOriginalRevenue: 0,
        increasedRevenue: 0,
        cost: 0,
        originalRevenue: 0,

        totalRequests: 0,
        protectedRequests: 0,
        boostingRequests: 0,
        onboardedRequests: 0,
      };
    }
    let {
      date,
      notProtectedRevenue,
      protectedOriginalRevenue,
      increasedRevenue,
      cost,
      originalRevenue,

      totalRequests,
      protectedRequests,
      boostingRequests,
      onboardedRequests,
    } = r;

    // currency is different per month
    const month = date.substring(0, "2020-12".length);
    const currencyRate = exchangeRates[month];

    increasedRevenue = _normalizeRevenue(increasedRevenue * currencyRate);
    originalRevenue = _normalizeRevenue(originalRevenue * currencyRate);
    notProtectedRevenue = _normalizeRevenue(notProtectedRevenue * currencyRate);
    protectedOriginalRevenue = _normalizeRevenue(
      protectedOriginalRevenue * currencyRate,
    );
    cost = _normalizeRevenue(cost * currencyRate);

    dataMap[r.date].increasedRevenue += _.round(increasedRevenue, 2);
    dataMap[r.date].originalRevenue += _.round(originalRevenue, 2);
    dataMap[r.date].notProtectedRevenue += _.round(notProtectedRevenue, 2);
    dataMap[r.date].protectedOriginalRevenue += _.round(
      protectedOriginalRevenue,
      2,
    );
    dataMap[r.date].cost += _.round(cost, 2);

    dataMap[r.date].totalRequests += totalRequests;
    dataMap[r.date].protectedRequests += protectedRequests;
    dataMap[r.date].boostingRequests += boostingRequests;
    dataMap[r.date].onboardedRequests += onboardedRequests;
  });

  const rrr = _.map(dataMap, (r) => {
    const revenueWithYB =
      r.notProtectedRevenue + r.protectedOriginalRevenue + r.increasedRevenue;
    const revenueWithoutYB = r.notProtectedRevenue + r.protectedOriginalRevenue;

    const netIncreasedRevenue = r.increasedRevenue - r.cost;

    const perceivedLift =
      r.protectedOriginalRevenue > 0
        ? (netIncreasedRevenue / r.protectedOriginalRevenue) * 100
        : 0;

    r.perceivedLift = _.round(perceivedLift, 2);
    r.revenueWithYB = _.round(revenueWithYB, 2);
    r.revenueWithoutYB = _.round(revenueWithoutYB, 2);

    // // Important! filter out data from when we are not optimizing but has a tiny bit of request
    // if (r.managedScore < 0.1) {
    //   r.grossRevenueLift = 0;
    //   r.perceivedLift = 0;
    //   r.rRpmWithYB = 0;
    //   r.rRpmWithoutYB = 0;
    // }

    return r;
  });

  return _.orderBy(rrr, ["dateMs"], ["asc"]);
}

function _processMetrics(itemsMap) {
  let totalGoogleAdRevenue = 0;
  let totalNetIncreasedRevenue = 0;
  let items = _.map(itemsMap, (item) => {
    const {
      notProtectedRevenue,
      protectedOriginalRevenue,
      increasedRevenue,
      cost,
    } = item;
    const googleAdRevenue =
      notProtectedRevenue + protectedOriginalRevenue + increasedRevenue;
    const netIncreasedRevenue = increasedRevenue - cost;

    totalGoogleAdRevenue += googleAdRevenue;
    totalNetIncreasedRevenue += netIncreasedRevenue;

    return {
      ..._.omit(item, [
        "notProtectedRevenue",
        "protectedOriginalRevenue",
        "increasedRevenue",
        "cost",
      ]),
      googleAdRevenue: _.round(_normalizeRevenue(googleAdRevenue), 2),
      netIncreasedRevenue: _.round(_normalizeRevenue(netIncreasedRevenue), 2),
    };
  });
  totalGoogleAdRevenue = _normalizeRevenue(totalGoogleAdRevenue);
  totalNetIncreasedRevenue = _normalizeRevenue(totalNetIncreasedRevenue);
  items = _.map(items, (item) => {
    item.googleAdRevenuePercentage = _.round(
      (item.googleAdRevenue / totalGoogleAdRevenue) * 100,
      2,
    );
    item.netIncreasedRevenuePercentage = _.round(
      (item.netIncreasedRevenue / totalNetIncreasedRevenue) * 100,
      2,
    );
    return item;
  });

  return items;
}

// V4
export function calculateDistributionData({
  homeDashboardData,
  selectedNetworkId, // highlight
  selectedYieldSetIds, // highlight
  selectedRequestType,
  startDate, // filter
  endDate, // filter
  exchangeRates,
}) {
  // notProtectedRevenue, protectedOriginalRevenue, increasedRevenue, cost
  // For "Ad Revenue Distribution" (notProtectedRevenue + protectedOriginalRevenue + increasedRevenue)
  // For "Net Increased Revenue Distribution" (increasedRevenue - cost)
  let publishersMap = {};
  let networksMap = {};
  let yieldSetsMap = {};
  let requestTypesMap = {};

  _.forEach(homeDashboardData.yieldSetPerformanceReport, (r) => {
    if (!_isDateBetween({ date: r.date, startDate, endDate })) {
      return;
    }

    // currency is different per month
    const month = r.date.substring(0, "2020-10".length);
    const currencyRate = exchangeRates[month];

    const notProtectedRevenue = r.notProtectedRevenue * currencyRate;
    const protectedOriginalRevenue = r.protectedOriginalRevenue * currencyRate;
    const increasedRevenue = r.increasedRevenue * currencyRate;
    const cost = r.cost * currencyRate;

    if (
      (selectedNetworkId === -1 || selectedNetworkId === r.gamNetworkId) &&
      (selectedYieldSetIds === -1 ||
        selectedYieldSetIds.indexOf(r.yieldSetId) !== -1)
    ) {
      if (!requestTypesMap[r.requestType]) {
        requestTypesMap[r.requestType] = {
          id: r.requestType,
          name: r.requestType,
          notProtectedRevenue,
          protectedOriginalRevenue,
          increasedRevenue,
          cost,
        };
      } else {
        requestTypesMap[r.requestType].notProtectedRevenue +=
          notProtectedRevenue;
        requestTypesMap[r.requestType].protectedOriginalRevenue +=
          protectedOriginalRevenue;
        requestTypesMap[r.requestType].increasedRevenue += increasedRevenue;
        requestTypesMap[r.requestType].cost += cost;
      }
    }

    if (
      _.isArray(selectedRequestType) &&
      selectedRequestType.indexOf(r.requestType) === -1
    ) {
      return;
    }

    if (!publishersMap[r.pubId]) {
      publishersMap[r.pubId] = {
        id: r.pubId,
        name: _.find(homeDashboardData.publishers, { id: r.pubId }).name,
        notProtectedRevenue,
        protectedOriginalRevenue,
        increasedRevenue,
        cost,
      };
    } else {
      publishersMap[r.pubId].notProtectedRevenue += notProtectedRevenue;
      publishersMap[r.pubId].protectedOriginalRevenue +=
        protectedOriginalRevenue;
      publishersMap[r.pubId].increasedRevenue += increasedRevenue;
      publishersMap[r.pubId].cost += cost;
    }

    if (!networksMap[r.gamNetworkId]) {
      networksMap[r.gamNetworkId] = {
        id: r.gamNetworkId,
        name: _.find(homeDashboardData.gamNetworks, { id: r.gamNetworkId })
          .name,
        pubId: r.pubId, // for later filtering purposes
        notProtectedRevenue,
        protectedOriginalRevenue,
        increasedRevenue,
        cost,
      };
    } else {
      networksMap[r.gamNetworkId].notProtectedRevenue += notProtectedRevenue;
      networksMap[r.gamNetworkId].protectedOriginalRevenue +=
        protectedOriginalRevenue;
      networksMap[r.gamNetworkId].increasedRevenue += increasedRevenue;
      networksMap[r.gamNetworkId].cost += cost;
    }

    if (!yieldSetsMap[r.yieldSetId]) {
      yieldSetsMap[r.yieldSetId] = {
        id: r.yieldSetId,
        name: _.find(homeDashboardData.yieldSets, { id: r.yieldSetId }).name,
        gamNetworkId: r.gamNetworkId,
        notProtectedRevenue,
        protectedOriginalRevenue,
        increasedRevenue,
        cost,
      };
    } else {
      yieldSetsMap[r.yieldSetId].notProtectedRevenue += notProtectedRevenue;
      yieldSetsMap[r.yieldSetId].protectedOriginalRevenue +=
        protectedOriginalRevenue;
      yieldSetsMap[r.yieldSetId].increasedRevenue += increasedRevenue;
      yieldSetsMap[r.yieldSetId].cost += cost;
    }
  });

  // publishers: [{ id, name, metric... }]
  // networks: [{ id, name, metric... }]
  // yieldSets: [{ id, name, metric... }]
  const publishers = _processMetrics(publishersMap);
  const networks = _processMetrics(networksMap);
  const yieldSets = _processMetrics(yieldSetsMap);
  const requestTypes = _processMetrics(requestTypesMap);

  return {
    publishers: _.orderBy(publishers, ["googleAdRevenue"], ["desc"]),
    networks: _.orderBy(networks, ["googleAdRevenue"], ["desc"]),
    yieldSets: _.orderBy(yieldSets, ["googleAdRevenue"], ["desc"]),
    requestTypes: _.orderBy(requestTypes, ["googleAdRevenue"], ["desc"]),
  };
}

// V4
export function calculateSummaryData({
  homeDashboardData,
  selectedPubId,
  selectedNetworkId,
  selectedYieldSetIds,
  selectedRequestType,
  startDate,
  endDate,
  exchangeRates,
}) {
  // "totalRequests", "protectedRequests", "boostingRequests",
  // "notProtectedRevenue", "protectedOriginalRevenue", "originalRevenue",
  // "increasedRevenue", "cost"
  let reports = [];

  if (selectedPubId === -1) {
    reports = _.filter(homeDashboardData.gamNetworkPerformanceReport, (r) => {
      return _isDateBetween({ date: r.date, startDate, endDate });
    });
  } else if (
    selectedPubId !== -1 &&
    selectedNetworkId === -1 &&
    selectedYieldSetIds === -1
  ) {
    reports = _.filter(homeDashboardData.gamNetworkPerformanceReport, (r) => {
      return (
        _isDateBetween({ date: r.date, startDate, endDate }) &&
        r.pubId === selectedPubId
      );
    });
  } else if (selectedNetworkId !== -1 && selectedYieldSetIds === -1) {
    reports = _.filter(homeDashboardData.gamNetworkPerformanceReport, (r) => {
      return (
        _isDateBetween({ date: r.date, startDate, endDate }) &&
        r.gamNetworkId === selectedNetworkId
      );
    });
  } else if (selectedYieldSetIds !== -1) {
    reports = _.filter(homeDashboardData.yieldSetPerformanceReport, (r) => {
      return (
        _isDateBetween({ date: r.date, startDate, endDate }) &&
        _.indexOf(selectedYieldSetIds, r.yieldSetId) !== -1
      );
    });
  }

  // filter by request type
  if (selectedRequestType !== -1) {
    reports = _.filter(reports, (r) => {
      return _.indexOf(selectedRequestType, r.requestType) !== -1;
    });
  }

  let data = {
    increasedRevenue: 0,
    cost: 0,
    notProtectedRevenue: 0,
    protectedOriginalRevenue: 0,
    originalRevenue: 0,

    totalRequests: 0,
    protectedRequests: 0,
    boostingRequests: 0,
    onboardedRequests: 0,
  };

  _.forEach(reports, (r) => {
    // currency is different per month
    const month = r.date.substring(0, "2020-10".length);
    const currencyRate = exchangeRates[month];

    data.increasedRevenue += r.increasedRevenue * currencyRate;
    data.cost += r.cost * currencyRate;
    data.notProtectedRevenue += r.notProtectedRevenue * currencyRate;
    data.protectedOriginalRevenue += r.protectedOriginalRevenue * currencyRate;
    data.originalRevenue += r.originalRevenue * currencyRate;

    data.totalRequests += r.totalRequests;
    data.protectedRequests += r.protectedRequests;
    data.boostingRequests += r.boostingRequests;
    data.onboardedRequests += r.onboardedRequests;
  });

  data.protectedRate = _.round(
    (data.protectedRequests / data.onboardedRequests) * 100,
    2,
  );
  data.defendedRate = _.round(
    (data.boostingRequests / data.protectedRequests) * 100,
    2,
  );

  data.netIncreasedRevenue = data.increasedRevenue - data.cost;

  const perceivedLift =
    data.protectedOriginalRevenue > 0
      ? (data.netIncreasedRevenue / data.protectedOriginalRevenue) * 100
      : 0;

  const securedLift =
    data.originalRevenue > 0
      ? (data.netIncreasedRevenue / data.originalRevenue) * 100
      : 0;

  data.netIncreasedRevenue = _.round(
    _normalizeRevenue(data.netIncreasedRevenue),
    2,
  );
  data.perceivedLift = _.round(perceivedLift, 2);
  data.securedLift = _.round(securedLift, 2);

  return data;
}

function _isDateBetween({ date, startDate, endDate }) {
  return moment(date).isBetween(startDate, endDate, undefined, "[]");
}

function _normalizeRevenue(revenue) {
  return revenue / 1000000;
}

function _transformExchangeRates(exchangeRates) {
  /*
  {
    USD: {
      "2020-09": 1
    },
    ...
  }
  */
  let currencyMap = {};
  _.forEach(exchangeRates, (e) => {
    currencyMap[e.currency] = _.reduce(
      e.rates,
      (result, r) => {
        const month = r[0];
        const rate = r[1];
        result[month] = rate;
        return result;
      },
      {},
    );
  });

  return currencyMap;
}

function _transformToJson(reports) {
  // transform reports to json format
  const { cols, records } = reports;

  return _.reduce(
    records,
    (result, row) => {
      let r = {};
      _.forEach(cols, (column, index) => {
        r[column] = row[index];
      });
      result.push(r);
      return result;
    },
    [],
  );
}
